import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, ChevronRight, Copy, Check, FileText, Shuffle, ArrowLeft } from "lucide-react";
import { PromptActionsMenu } from "@/components/shared/PromptActionsMenu";
import { useIsMobile } from "@/hooks/use-mobile";
import { getScoreColor } from "@/lib/scoreUtils";
import { ScoreBreakdown } from "@/components/results/ScoreBreakdown";
import { FavoriteButton } from "@/components/shared/FavoriteButton";
import { CompactScoreBar } from "@/components/shared/CompactScoreBar";

interface PromptDetailData {
  score_id: string;
  overall_score: number;
  original_score?: number | null;
  detected_type: string | null;
  best_prompt_extracted: string | null;
  dimension_scores_json: Record<string, any> | null;
  evaluation_markdown: string | null;
  chatcraft_subscores_json: Record<string, number> | null;
  original_content: string | null;
  submission_type: string | null;
  points_total: number | null;
  points_base?: number | null;
  points_rubric?: number | null;
  points_bonus?: number | null;
  user_name?: string | null;
  created_at: string;
  tags?: string[];
  submission_id?: string | null;
  is_public?: boolean | null;
  user_id?: string | null;
}

interface PromptDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: PromptDetailData | null;
  loading?: boolean;
  showUserName?: boolean;
  onRemix?: (data: PromptDetailData) => void;
  onHidePrompt?: () => void;
  onDeletePrompt?: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  Planning: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  Strategy: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  Research: "bg-green-500/20 text-green-300 border-green-500/30",
  Drafting: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  Analysis: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  Creative: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  Brainstorming: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  Summarizing: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface ImprovedPromptDisplayProps {
  content: string;
  onRemix?: () => void;
  showRemix: boolean;
}

function ImprovedPromptDisplay({ content, onRemix, showRemix }: ImprovedPromptDisplayProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const chatGptUrl = `https://chatgpt.com/?prompt=${encodeURIComponent(content)}`;

  return (
    <div className="space-y-2 min-w-0">
      {/* Header with label and action buttons */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium">Improved Prompt</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8" asChild onClick={(e) => e.stopPropagation()}>
            <a href={chatGptUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" />
              ChatGPT
            </a>
          </Button>
          {showRemix && onRemix && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={(e) => {
                e.stopPropagation();
                onRemix();
              }}
            >
              <Shuffle className="h-4 w-4 mr-1" />
              Remix
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy} className="h-8">
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
          </Button>
        </div>
      </div>
      {/* Content box */}
      <div className="p-3 rounded-lg bg-muted/50 border border-border max-h-24 overflow-y-auto">
        <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
      </div>
    </div>
  );
}

export function PromptDetailModal({
  isOpen,
  onClose,
  data,
  loading = false,
  showUserName = false,
  onRemix,
  onHidePrompt,
  onDeletePrompt,
}: PromptDetailModalProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const isChat = data?.submission_type === "chat_full";

  // Parse evaluation_markdown once for all derived values
  let promptSummary = "";
  let promptTitle = "";
  let effectiveImprovedPrompt = data?.best_prompt_extracted || "";
  if (data?.evaluation_markdown) {
    try {
      const parsed = JSON.parse(data.evaluation_markdown);
      promptSummary = parsed.prompt_summary || "";
      promptTitle = parsed.prompt_title || "";
      effectiveImprovedPrompt = parsed.rewrite_clean || parsed.best_prompt_extracted || data.best_prompt_extracted || "";
    } catch (e) {
      // Silently fail if not valid JSON
    }
  }

  const handleSeeFullDetails = () => {
    if (!data) return;

    // Parse evaluation_markdown to get full analysis data
    let parsedAnalysis: Record<string, any> = {};
    if (data.evaluation_markdown) {
      try {
        parsedAnalysis = JSON.parse(data.evaluation_markdown);
      } catch (e) {
        console.warn("Failed to parse evaluation_markdown:", e);
      }
    }

    const historicalResult = {
      ui: {
        input_kind: isChat ? "chat_full" : "single_prompt",
        detected_type: data.detected_type || parsedAnalysis.detected_type || "Analysis",
        why_type: parsedAnalysis.why_type || "",
        original_text: data.original_content || parsedAnalysis.original_text || "",
        whats_working: parsedAnalysis.whats_working || [],
        improvements: parsedAnalysis.improvements || [],
        rewrite_tagged: parsedAnalysis.rewrite_tagged || data.best_prompt_extracted || "",
        rewrite_clean: parsedAnalysis.rewrite_clean || data.best_prompt_extracted || "",
        bubbles: parsedAnalysis.bubbles || [],
        quick_wins: parsedAnalysis.quick_wins || [],
        progress_points: {
          submission_points: data.points_base || 5,
          bonus_points: data.points_bonus || 0,
          total_points: data.points_total || 0,
          bonus_explainer: "",
          suggested_next_move: "",
        },
        dimension_scores: data.dimension_scores_json || parsedAnalysis.dimension_scores,
        top_upgrades: parsedAnalysis.top_upgrades || [],
        prompt_summary: parsedAnalysis.prompt_summary || "",
        prompt_title: parsedAnalysis.prompt_title || "",
        score_id: data.score_id,
        submission_id: data.submission_id,
        is_public: data.is_public ?? false,
        user_id: data.user_id,
        original_score: data.original_score ?? parsedAnalysis.original_score,
        improved_score: data.overall_score ?? parsedAnalysis.improved_score,
      },
      telemetry: {
        tags: data.tags || parsedAnalysis.tags || [],
      },
    };

    onClose();
    navigate(`/prompt_score/${data.score_id}`, { state: { result: historicalResult } });
  };

  // NOTE: DialogContent base styles include `grid` (from our shared dialog primitive).
  // Tailwind's `grid` utility can win over `flex` due to CSS generation order, which breaks
  // flex-based height constraints and prevents ScrollArea from ever detecting overflow.
  // We force `display:flex` here to guarantee ScrollArea gets a real constrained height.
  const desktopClasses = "max-w-2xl max-h-[85vh] min-h-0 p-0 gap-0 !flex !flex-col";
  const mobileClasses =
    "fixed inset-0 w-full h-full max-w-none max-h-none rounded-none border-0 p-0 gap-0 translate-x-0 translate-y-0 top-0 left-0 !flex !flex-col";

  // Sticky header content (shared between mobile and desktop when data is loaded)
  const renderStickyHeader = () => {
    if (!data) return null;
    return (
      <div className="space-y-3 min-w-0">
        {/* Row 1: Time ... Star (anonymous feed) */}
        <div className="flex items-center justify-between gap-2 min-w-0 w-full">
          <span className="text-muted-foreground text-sm">
            {formatRelativeTime(data.created_at)}
          </span>
          <div className="shrink-0 flex items-center gap-1">
            {data.submission_id && data.user_id && (
              <PromptActionsMenu
                submissionId={data.submission_id}
                promptUserId={data.user_id}
                isPublic={data.is_public ?? true}
                onHide={() => {
                  onClose();
                  onHidePrompt?.();
                }}
                onDelete={() => {
                  onClose();
                  onDeletePrompt?.();
                }}
              />
            )}
            <FavoriteButton scoreId={data.score_id} size="md" />
          </div>
        </div>

        {/* Row 2: Type badge + Tags (scrollable) + See full details */}
        <div className="flex items-center gap-2 w-full min-w-0">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-1 min-w-0">
            {data.detected_type && (
              <Badge
                variant="outline"
                className={`text-xs border shrink-0 rounded-md ${CATEGORY_COLORS[data.detected_type] || CATEGORY_COLORS["Unknown"]}`}
              >
                {data.detected_type}
              </Badge>
            )}
            {data.tags &&
              data.tags.map((tag, i) => (
                <Badge key={i} variant="secondary" className="text-xs shrink-0 whitespace-nowrap">
                  {tag}
                </Badge>
              ))}
          </div>
          <Button variant="ghost" size="sm" onClick={handleSeeFullDetails} className="shrink-0 text-xs px-2">
            See full details
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={isMobile ? mobileClasses : desktopClasses}>
        {/* Sticky header - mobile has back arrow, desktop just has content */}
        {isMobile ? (
          <div className="flex items-center gap-3 p-4 border-b border-border sticky top-0 bg-background z-10 shrink-0">
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <span className="font-semibold">Prompt Details</span>
          </div>
        ) : (
          data && (
            <div className="px-6 pt-6 pb-3 border-b border-border/50 shrink-0">
              {renderStickyHeader()}
            </div>
          )
        )}

        {/* Scrollable content area - native CSS overflow */}
        <div className={isMobile ? "flex-1 overflow-y-auto min-h-0 px-4 pt-4 pb-8" : "flex-1 overflow-y-auto min-h-0 px-6 pb-6 pt-4"}>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : data ? (
            <div className="space-y-5 min-w-0">
              {/* Mobile: show header inline since mobile sticky header is just title */}
              {isMobile && renderStickyHeader()}

              {/* Score Section */}
              <Card className="bg-muted/30 border-border/50">
                <CardContent className="p-4 space-y-3">
                  {/* Score Comparison Bar or static score */}
                  {data.original_score ? (
                    <CompactScoreBar inputScore={data.original_score} enhancedScore={data.overall_score} />
                  ) : (
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Rubric Score</p>
                        <p className={`text-lg font-bold ${getScoreColor(data.overall_score)}`}>
                          {data.overall_score}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Title + Summary */}
                  {(promptTitle || promptSummary) && (
                    <div className="border-t border-border/50 pt-3">
                      {promptTitle && <h3 className="text-sm font-semibold text-foreground mb-1">{promptTitle}</h3>}
                      {promptSummary && <p className="text-sm text-foreground/80">{promptSummary}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Improved Prompt with inline actions */}
              {effectiveImprovedPrompt && (
                <ImprovedPromptDisplay
                  content={effectiveImprovedPrompt}
                  onRemix={onRemix ? () => onRemix({ ...data, best_prompt_extracted: effectiveImprovedPrompt }) : undefined}
                  showRemix={!!onRemix}
                />
              )}

              {/* Score breakdown */}
              {data.dimension_scores_json && (
                <div className="min-w-0 w-full">
                  <ScoreBreakdown
                    overallScore={data.overall_score}
                    evaluationMarkdown={data.evaluation_markdown || ""}
                    dimensionScores={data.dimension_scores_json}
                    isChat={isChat}
                    chatcraftScoresJson={data.chatcraft_subscores_json || undefined}
                    collapsible={true}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">Failed to load prompt details</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
